// Comprehensive Certification System Service — delegates to repositories.
import { DRXError } from '@/lib/drx-error';
import {
  Certification,
  CertificationModule,
  CertificationCourse,
  Lesson,
  Quiz,
  CreateCertificationData,
  CreateCertificationModuleData,
  CreateCertificationCourseData,
  CreateLessonData as CertCreateLessonData,
  CreateQuizData,
} from './types';
import { CourseRepository, type CreateCourseData } from '../repository/course.repository';
import type { UpdateCourseData } from '../repository/interfaces/course.repository.interface';
import { ModuleRepository, type CreateModuleData, type GetModuleData } from '../repository/module.repository';
import { LessonRepository, type CreateLessonData as RepoCreateLessonData, type GetLessonData } from '../repository/lesson.repository';
import { ResourceRepository } from '../repository/resource.repository';
import { QuizRepository } from '../repository/quiz.repository';

export class CertificationSystemService {
  constructor(
    private readonly courseRepository: CourseRepository,
    private readonly moduleRepository: ModuleRepository,
    private readonly lessonRepository: LessonRepository,
    private readonly resourceRepository: ResourceRepository,
    private readonly quizRepository: QuizRepository,
  ) {}

  private mapModule(m: GetModuleData): CertificationModule {
    const raw = m as GetModuleData & {
      certificationId?: string;
      sequence?: number;
      completionCriteria?: string[];
      status?: CertificationModule['status'];
      durationHours?: number;
      isRequired?: boolean;
    };
    const certId = raw.courseId || raw.certificationId || '';
    const sequence = raw.orderIndex ?? raw.sequence ?? 0;
    const durationHours =
      raw.estimatedDurationMinutes != null
        ? raw.estimatedDurationMinutes / 60
        : raw.durationHours ?? 0;
    return {
      id: m.id,
      title: m.title,
      description: m.description || '',
      certificationId: certId,
      sequence,
      durationHours,
      isRequired: raw.isRequired ?? true,
      completionCriteria: raw.completionCriteria ?? raw.prerequisites ?? [],
      status: raw.status ?? (m.isPublished ? 'published' : 'draft'),
      createdAt: m.createdAt,
      updatedAt: m.updatedAt,
    };
  }

  private mapLesson(l: GetLessonData): Lesson {
    const raw = l as GetLessonData & {
      type?: Lesson['type'];
      isRequired?: boolean;
      contentUrl?: string;
      contentText?: string;
      status?: Lesson['status'];
      resources?: string[];
      transcript?: string;
    };
    return {
      id: l.id,
      title: l.title,
      description: l.description || '',
      courseId: l.courseId,
      type: raw.type ?? 'video',
      orderIndex: l.orderIndex,
      durationMinutes: l.durationMinutes ?? 0,
      isRequired: raw.isRequired ?? !l.isPreview,
      contentUrl: raw.contentUrl,
      videoUrl: l.videoUrl,
      contentText: raw.contentText ?? l.content,
      resources: raw.resources,
      transcript: raw.transcript,
      status: raw.status ?? (l.isPublished ? 'published' : 'draft'),
      createdAt: l.createdAt,
      updatedAt: l.updatedAt,
    };
  }

  async createCertification(data: CreateCertificationData): Promise<Certification> {
    console.log('[CertificationSystemService] Creating course (certification):', data);
    try {
      const coursePayload = {
        title: data.title,
        description: data.description,
        price: 0,
        instructorId: data.instructorId,
        imageUrl: data.imageUrl,
        previewVideoUrl: '',
        videoUrl: '',
        detailedDescription: data.description,
        difficultyLevel: data.difficultyLevel,
        duration: `${data.estimatedDurationHours}h`,
        visible: data.isPublished,
        isPublished: data.isPublished,
        categoryIds: data.category ? [data.category] : [],
        requirements: data.requirements,
        benefits: data.benefits,
        category: data.category,
        enrollmentCount: 0,
        completionCount: 0,
        completionRate: 0,
        isFeatured: data.isFeatured ?? false,
        orderIndex: data.orderIndex,
      } as CreateCourseData;

      const created = await this.courseRepository.createCourse(coursePayload);
      console.log('[CertificationSystemService] Created certification:', created.id);
      return created as unknown as Certification;
    } catch (error) {
      console.error('[CertificationSystemService] Error creating certification:', error);
      throw new DRXError({
        code: 'CERTIFICATION_CREATE_FAILED',
        devMessage: 'Failed to create certification',
        cause: error,
      });
    }
  }

  async getCertifications(instructorId?: string): Promise<Certification[]> {
    console.log('[CertificationSystemService] Getting courses (certifications), instructorId:', instructorId);
    try {
      const list = instructorId
        ? await this.courseRepository.getCoursesByInstructor(instructorId, 500)
        : await this.courseRepository.getCourses({}, 500);
      console.log('[CertificationSystemService] Retrieved', list.length, 'certifications');
      return list as unknown as Certification[];
    } catch (error) {
      console.error('[CertificationSystemService] Error getting certifications:', error);
      throw new DRXError({
        code: 'CERTIFICATION_LIST_FAILED',
        devMessage: 'Failed to get certifications',
        cause: error,
      });
    }
  }

  async getCertificationById(certificationId: string): Promise<Certification | null> {
    try {
      const course = await this.courseRepository.getCourseById(certificationId);
      return (course as unknown as Certification) ?? null;
    } catch (error) {
      console.error('[CertificationSystemService] Error getting course (certification):', error);
      return null;
    }
  }

  async updateCertification(certificationId: string, data: Partial<CreateCertificationData>): Promise<void> {
    try {
      await this.courseRepository.updateCourse(certificationId, data as UpdateCourseData);
    } catch (error) {
      console.error('[CertificationSystemService] Error updating certification:', error);
      throw new DRXError({
        code: 'CERTIFICATION_UPDATE_FAILED',
        devMessage: 'Failed to update certification',
        cause: error,
      });
    }
  }

  async deleteCertification(certificationId: string): Promise<void> {
    try {
      await this.courseRepository.deleteCourse(certificationId);
    } catch (error) {
      console.error('[CertificationSystemService] Error deleting course (certification):', error);
      throw new DRXError({
        code: 'CERTIFICATION_DELETE_FAILED',
        devMessage: 'Failed to delete course',
        cause: error,
      });
    }
  }

  async createModule(data: CreateCertificationModuleData): Promise<CertificationModule> {
    console.log('[CertificationSystemService] Creating module:', data);
    try {
      const row: CreateModuleData = {
        title: data.title,
        description: data.description,
        courseId: data.certificationId,
        certificationId: data.certificationId,
        orderIndex: data.sequence,
        sequence: data.sequence,
        isPublished: false,
        estimatedDurationMinutes: Math.round((data.durationHours ?? 0) * 60),
        prerequisites: data.completionCriteria,
        completionCriteria: data.completionCriteria,
        learningObjectives: [],
        status: 'draft',
        isRequired: data.isRequired,
      };
      const created = await this.moduleRepository.createModule(row);
      console.log('[CertificationSystemService] Created module:', created.id);
      return this.mapModule(created);
    } catch (error) {
      console.error('[CertificationSystemService] Error creating module:', error);
      throw new DRXError({
        code: 'CERTIFICATION_MODULE_CREATE_FAILED',
        devMessage: 'Failed to create module',
        cause: error,
      });
    }
  }

  async getModulesByCourse(courseId: string): Promise<CertificationModule[]> {
    try {
      const modules = await this.moduleRepository.getModulesByCourse(courseId);
      return modules.map((m) => this.mapModule(m));
    } catch (error) {
      console.error('[CertificationSystemService] Error getting modules:', error);
      return [];
    }
  }

  async getModules(instructorId?: string): Promise<CertificationModule[]> {
    try {
      const modules = instructorId
        ? await this.moduleRepository.getModulesByOwner(instructorId, 2000)
        : await this.moduleRepository.getAllModules(2000);
      return modules.map((m) => this.mapModule(m));
    } catch (error) {
      console.error('[CertificationSystemService] Error getting modules:', error);
      return [];
    }
  }

  async createCourse(data: CreateCertificationCourseData): Promise<CertificationCourse> {
    console.log('[CertificationSystemService] Creating course:', data);
    try {
      const coursePayload = {
        title: data.title,
        description: data.description,
        price: data.price,
        instructorId: data.instructorId,
        imageUrl: data.imageUrl,
        previewVideoUrl: data.previewVideoUrl ?? '',
        videoUrl: '',
        detailedDescription: data.detailedDescription ?? data.description,
        difficultyLevel: data.difficultyLevel,
        duration: `${data.durationHours}h`,
        visible: data.isPublished,
        isPublished: data.isPublished,
        categoryIds: data.categoryId ? [data.categoryId] : [],
        prerequisites: data.prerequisites,
        requirements: data.requirements,
        moduleId: data.moduleId,
        certificationId: data.certificationId,
        moduleSequence: data.moduleSequence,
        enrollmentCount: 0,
        completionRate: 0,
      } as CreateCourseData;

      const created = await this.courseRepository.createCourse(coursePayload);
      console.log('[CertificationSystemService] Created course:', created.id);
      return created as unknown as CertificationCourse;
    } catch (error) {
      console.error('[CertificationSystemService] Error creating course:', error);
      throw new DRXError({
        code: 'CERTIFICATION_COURSE_CREATE_FAILED',
        devMessage: 'Failed to create course',
        cause: error,
      });
    }
  }

  async getCourses(instructorId?: string): Promise<CertificationCourse[]> {
    try {
      const list = instructorId
        ? await this.courseRepository.getCoursesByInstructor(instructorId, 500)
        : await this.courseRepository.getCourses({}, 500);
      return list as unknown as CertificationCourse[];
    } catch (error) {
      console.error('[CertificationSystemService] Error getting courses:', error);
      return [];
    }
  }

  async getCoursesByModule(moduleId: string): Promise<CertificationCourse[]> {
    try {
      const list = await this.courseRepository.getCoursesByModuleId(moduleId, 500);
      return list as unknown as CertificationCourse[];
    } catch (error) {
      console.error('[CertificationSystemService] Error getting courses by module:', error);
      return [];
    }
  }

  async createLesson(data: CertCreateLessonData): Promise<Lesson> {
    console.log('[CertificationSystemService] Creating lesson:', data);
    try {
      const ext = data as CertCreateLessonData & { moduleId?: string };
      const moduleId = ext.moduleId ?? '';
      const row: RepoCreateLessonData = {
        title: data.title,
        description: data.description,
        content: data.contentText ?? data.description ?? '',
        videoUrl: data.videoUrl ?? data.contentUrl,
        durationMinutes: data.durationMinutes,
        moduleId,
        courseId: data.courseId,
        orderIndex: data.orderIndex,
        isPreview: !data.isRequired,
        isPublished: false,
      };
      const created = await this.lessonRepository.createLesson(row);
      console.log('[CertificationSystemService] Created lesson:', created.id);
      return this.mapLesson(created);
    } catch (error) {
      console.error('[CertificationSystemService] Error creating lesson:', error);
      throw new DRXError({
        code: 'CERTIFICATION_LESSON_CREATE_FAILED',
        devMessage: 'Failed to create lesson',
        cause: error,
      });
    }
  }

  async getLessonsByModule(moduleId: string): Promise<Lesson[]> {
    try {
      const lessons = await this.lessonRepository.getLessonsByModule(moduleId);
      return lessons.map((l) => this.mapLesson(l));
    } catch (error) {
      console.error('[CertificationSystemService] Error getting lessons:', error);
      return [];
    }
  }

  async getLessons(instructorId?: string): Promise<Lesson[]> {
    try {
      const lessons = instructorId
        ? await this.lessonRepository.getLessonsByOwner(instructorId, 2000)
        : await this.lessonRepository.getLessons({}, 2000);
      return lessons.map((l) => this.mapLesson(l));
    } catch (error) {
      console.error('[CertificationSystemService] Error getting lessons:', error);
      return [];
    }
  }

  async createQuiz(data: CreateQuizData): Promise<Quiz> {
    console.log('[CertificationSystemService] Creating quiz:', data);
    try {
      const created = await this.quizRepository.createQuiz({
        title: data.title,
        description: data.description,
        courseId: data.courseId,
        lessonId: data.lessonId,
        timeLimit: data.timeLimit,
        passingScore: data.passingScore,
        maxAttempts: data.maxAttempts,
        status: 'draft',
        questionCount: data.questionCount,
      });
      console.log('[CertificationSystemService] Created quiz:', created.id);
      return created as unknown as Quiz;
    } catch (error) {
      console.error('[CertificationSystemService] Error creating quiz:', error);
      throw new DRXError({
        code: 'CERTIFICATION_QUIZ_CREATE_FAILED',
        devMessage: 'Failed to create quiz',
        cause: error,
      });
    }
  }

  async getQuizzesByModule(moduleId: string): Promise<Quiz[]> {
    try {
      const quizzes = await this.quizRepository.getQuizzesByModule(moduleId);
      return quizzes as unknown as Quiz[];
    } catch (error) {
      console.error('[CertificationSystemService] Error getting quizzes:', error);
      return [];
    }
  }

  async getQuizzes(instructorId?: string): Promise<Quiz[]> {
    try {
      if (instructorId) {
        const quizzes = await this.quizRepository.getQuizzesByInstructor(instructorId);
        return quizzes as unknown as Quiz[];
      }
      const quizzes = await this.quizRepository.getAllQuizzes();
      return quizzes as unknown as Quiz[];
    } catch (error) {
      console.error('[CertificationSystemService] Error getting quizzes:', error);
      return [];
    }
  }

  async getResourcesByCourse(courseId: string): Promise<Array<{ id: string } & Record<string, unknown>>> {
    try {
      const resources = await this.resourceRepository.listCourseCurriculumResourcesByCourseId(courseId);
      console.log('[CertificationSystemService] Retrieved', resources.length, 'resources for course:', courseId);
      return resources;
    } catch (error) {
      console.error('[CertificationSystemService] Error getting course resources:', error);
      throw new DRXError({
        code: 'CERTIFICATION_COURSE_RESOURCES_FAILED',
        devMessage: 'Failed to get course resources',
        cause: error,
      });
    }
  }

  async getCourseWithFullHierarchy(courseId: string): Promise<unknown> {
    try {
      const course = await this.getCertificationById(courseId);
      if (!course) return null;

      const resources = await this.getResourcesByCourse(courseId);
      const modules = await this.getModulesByCourse(courseId);

      const modulesWithContent = await Promise.all(
        modules.map(async (mod) => {
          const lessons = await this.getLessonsByModule(mod.id);
          const quizzes = await this.getQuizzesByModule(mod.id);

          return {
            ...mod,
            lessons,
            quizzes,
          };
        }),
      );

      return {
        ...course,
        modules: modulesWithContent,
        resources,
      };
    } catch (error) {
      console.error('[CertificationSystemService] Error getting course hierarchy:', error);
      throw new DRXError({
        code: 'CERTIFICATION_HIERARCHY_FAILED',
        devMessage: 'Failed to get course hierarchy',
        cause: error,
      });
    }
  }
}
